import type { RouteRecordRaw } from 'vue-router'

import { registerInsightMessages } from './i18n'

import { useLocaleStore } from '@/shared/i18n'

export const INSIGHT_WORKBENCH_ROUTE = { name: 'InsightWorkbench' } as const
export const INSIGHTS_PREVIEW_ROUTE = { name: 'InsightsPreview' } as const
export const INSIGHT_CONFIG_PREVIEW_LIST_ROUTE = { name: 'InsightConfigPreviewList' } as const
export const INSIGHT_CONFIG_PREVIEW_CREATE_ROUTE = { name: 'InsightConfigPreviewCreate' } as const
export const INSIGHT_CONFIG_PREVIEW_EDIT_ROUTE = { name: 'InsightConfigPreviewEdit' } as const
export const INSIGHT_AUTHORING_PREVIEW_ROUTE = { name: 'InsightAuthoringPreview' } as const
export const INSIGHT_AUTHORING_PREVIEW_NEW_ROUTE = { name: 'InsightAuthoringPreviewNew' } as const
export const INSIGHT_AUTHORING_PREVIEW_SESSION_ROUTE = {
  name: 'InsightAuthoringPreviewSession',
} as const

/**
 * The three settings nav entries `installInsightsSettingsNav` registers, reused as each record's
 * `navKey` so 22's tree keeps the right leaf active on the detail routes too (`create`, `:configId`).
 * The route names stay put: they are the registry's `routeName` and 22 matches on either.
 */
export const INSIGHT_SETTINGS_NAV_KEYS = {
  workbench: 'insights.workbench',
  configs: 'insights.configs',
  authoring: 'insights.authoring',
} as const

/**
 * The seven sysadmin records are registered as real `/settings` children. Their own PageHeader is
 * retained because its action slot carries the pane's primary controls (Save changes, Create,
 * Start), while `meta.ownsHeader` tells the host not to render a second heading.
 */
const allInsightRoutes: RouteRecordRaw[] = [
  {
    // The canonical consumer path (contract 15 §3): `/insights` is v3's URL too, and this record
    // wins it because `appRoutes` declares the '/' parent's children ahead of `legacyIngressRoutes`
    // — the same ordering `/data-export` relies on. The route NAME stays `InsightsPreview`: the
    // sidebar leaf and the registry's ingress record both target it by name, so renaming it is a
    // separate change with its own blast radius.
    path: 'insights',
    name: INSIGHTS_PREVIEW_ROUTE.name,
    component: () => import('./components/display/InsightsRoute.vue'),
    meta: {
      requiresAuth: true,
      navKey: 'insights',
      titleKey: 'insights.list.title',
      contentInset: 'spacious',
    },
    beforeEnter: () => registerInsightMessages(useLocaleStore().locale),
  },
  {
    // The PERMANENT home for this screen, not a verification seam: the module-19 `/admin` parent it
    // was a stand-in for is not being built (2026-07-31 product decision,
    // `app/router/ingress/pendingCutover.ts` marks `/admin/**` `activation:'permanent'`), which is
    // why `sysadmin/routes.ts` is deleted and why the `-preview` suffix is gone. Both are ORP-20405.
    // This route flips no ingress.
    path: 'settings/insights-workbench',
    name: INSIGHT_WORKBENCH_ROUTE.name,
    component: () => import('./components/sysadmin/workbench/SysadminInsightWorkbench.vue'),
    meta: {
      requiresAuth: true,
      requiresSysAdmin: true,
      settingsNav: true,
      navKey: INSIGHT_SETTINGS_NAV_KEYS.workbench,
      titleKey: 'insights.sysadmin.workbench.title',
      contentInset: 'spacious',
    },
    beforeEnter: () => registerInsightMessages(useLocaleStore().locale),
  },
  {
    path: 'settings/insight-configs',
    name: INSIGHT_CONFIG_PREVIEW_LIST_ROUTE.name,
    component: () => import('./components/sysadmin/config/InsightConfigList.vue'),
    props: {
      createRouteName: INSIGHT_CONFIG_PREVIEW_CREATE_ROUTE.name,
      editRouteName: INSIGHT_CONFIG_PREVIEW_EDIT_ROUTE.name,
    },
    meta: {
      requiresAuth: true,
      requiresSysAdmin: true,
      settingsNav: true,
      navKey: INSIGHT_SETTINGS_NAV_KEYS.configs,
      titleKey: 'insights.sysadmin.config.listTitle',
      contentInset: 'spacious',
    },
    beforeEnter: () => registerInsightMessages(useLocaleStore().locale),
  },
  {
    path: 'settings/insight-configs/create',
    name: INSIGHT_CONFIG_PREVIEW_CREATE_ROUTE.name,
    component: () => import('./components/sysadmin/config/InsightConfigEditor.vue'),
    props: {
      listRouteName: INSIGHT_CONFIG_PREVIEW_LIST_ROUTE.name,
      editRouteName: INSIGHT_CONFIG_PREVIEW_EDIT_ROUTE.name,
    },
    meta: {
      requiresAuth: true,
      requiresSysAdmin: true,
      settingsNav: true,
      navKey: INSIGHT_SETTINGS_NAV_KEYS.configs,
      titleKey: 'insights.sysadmin.config.createTitle',
      contentInset: 'spacious',
    },
    beforeEnter: () => registerInsightMessages(useLocaleStore().locale),
  },
  {
    path: 'settings/insight-configs/:configId',
    name: INSIGHT_CONFIG_PREVIEW_EDIT_ROUTE.name,
    component: () => import('./components/sysadmin/config/InsightConfigEditor.vue'),
    props: (route) => ({
      configId: Number(route.params.configId),
      listRouteName: INSIGHT_CONFIG_PREVIEW_LIST_ROUTE.name,
      editRouteName: INSIGHT_CONFIG_PREVIEW_EDIT_ROUTE.name,
    }),
    meta: {
      requiresAuth: true,
      requiresSysAdmin: true,
      settingsNav: true,
      navKey: INSIGHT_SETTINGS_NAV_KEYS.configs,
      titleKey: 'insights.sysadmin.config.editTitle',
      contentInset: 'spacious',
    },
    beforeEnter: () => registerInsightMessages(useLocaleStore().locale),
  },
  {
    path: 'settings/insight-authoring',
    name: INSIGHT_AUTHORING_PREVIEW_ROUTE.name,
    component: () => import('./components/sysadmin/authoring/InsightAuthoringLanding.vue'),
    props: {
      newRouteName: INSIGHT_AUTHORING_PREVIEW_NEW_ROUTE.name,
      sessionRouteName: INSIGHT_AUTHORING_PREVIEW_SESSION_ROUTE.name,
    },
    meta: {
      requiresAuth: true,
      requiresSysAdmin: true,
      settingsNav: true,
      navKey: INSIGHT_SETTINGS_NAV_KEYS.authoring,
      titleKey: 'insights.sysadmin.authoring.landingTitle',
      contentInset: 'spacious',
    },
    beforeEnter: () => registerInsightMessages(useLocaleStore().locale),
  },
  {
    path: 'settings/insight-authoring/new',
    name: INSIGHT_AUTHORING_PREVIEW_NEW_ROUTE.name,
    component: () => import('./components/sysadmin/authoring/InsightAuthoringWizard.vue'),
    props: {
      newRouteName: INSIGHT_AUTHORING_PREVIEW_NEW_ROUTE.name,
      sessionRouteName: INSIGHT_AUTHORING_PREVIEW_SESSION_ROUTE.name,
      configEditRouteName: INSIGHT_CONFIG_PREVIEW_EDIT_ROUTE.name,
    },
    meta: {
      requiresAuth: true,
      requiresSysAdmin: true,
      settingsNav: true,
      navKey: INSIGHT_SETTINGS_NAV_KEYS.authoring,
      titleKey: 'insights.sysadmin.authoring.newTitle',
      contentInset: 'spacious',
    },
    beforeEnter: () => registerInsightMessages(useLocaleStore().locale),
  },
  {
    path: 'settings/insight-authoring/:sessionId',
    name: INSIGHT_AUTHORING_PREVIEW_SESSION_ROUTE.name,
    component: () => import('./components/sysadmin/authoring/InsightAuthoringWizard.vue'),
    props: (route) => ({
      sessionId: String(route.params.sessionId),
      newRouteName: INSIGHT_AUTHORING_PREVIEW_NEW_ROUTE.name,
      sessionRouteName: INSIGHT_AUTHORING_PREVIEW_SESSION_ROUTE.name,
      configEditRouteName: INSIGHT_CONFIG_PREVIEW_EDIT_ROUTE.name,
    }),
    meta: {
      requiresAuth: true,
      requiresSysAdmin: true,
      settingsNav: true,
      navKey: INSIGHT_SETTINGS_NAV_KEYS.authoring,
      titleKey: 'insights.sysadmin.authoring.sessionTitle',
      contentInset: 'spacious',
    },
    beforeEnter: () => registerInsightMessages(useLocaleStore().locale),
  },
]

export const insightRoutes: RouteRecordRaw[] = allInsightRoutes.filter(
  (route) => route.name === INSIGHTS_PREVIEW_ROUTE.name,
)

/** Settings-host children are kept separate from the app-level route list. */
export const insightSettingsRoutes: RouteRecordRaw[] = allInsightRoutes
  .filter((route) => route.name !== INSIGHTS_PREVIEW_ROUTE.name)
  .map((route) => ({
    ...route,
    path: String(route.path).replace(/^settings\//u, ''),
    meta: { ...route.meta, ownsHeader: true },
  }))
